import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSite } from "../contexts/SiteContext";

function setMeta(attr, key, val) {
  if (!val) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", val);
}

function setCanonical(url) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", url);
}

/**
 * Single source of truth for document title/meta. Applies the global SEO, overridden
 * by a per-route override (set by CmsPage / usePageSeo via setPageSeo) when present.
 * Also keeps canonical + og:url in sync with the current route on every navigation.
 */
export default function Seo() {
  const { seo, pageSeo } = useSite();
  const { i18n } = useTranslation();
  const { pathname } = useLocation();
  const lang = i18n.language?.startsWith("fr") ? "fr" : "en";
  useEffect(() => {
    const L = (v) => (v && typeof v === "object" ? (v[lang] ?? v.en) : v);
    const title = (pageSeo && L(pageSeo.title)) || seo?.site_title;
    const desc = (pageSeo && L(pageSeo.description)) || seo?.description;
    // Le partage peut mériter un autre titre que l'onglet : une accroche plutôt
    // qu'un libellé de navigation.
    const ogTitle = (pageSeo && L(pageSeo.og_title)) || title;
    const ogDesc = (pageSeo && L(pageSeo.og_description)) || desc;
    if (title) { document.title = title; setMeta("property", "og:title", ogTitle); setMeta("name", "twitter:title", ogTitle); }
    if (desc) { setMeta("name", "description", desc); setMeta("property", "og:description", ogDesc); setMeta("name", "twitter:description", ogDesc); }
    const ogImage = (pageSeo && pageSeo.og_image) || seo?.og_image;
    setMeta("property", "og:image", ogImage);
    setMeta("name", "twitter:image", ogImage);
    setMeta("name", "twitter:card", "summary_large_image");
    if (seo?.twitter_handle) setMeta("name", "twitter:site", seo.twitter_handle);
    // Canonical + og:url track the current path (strip query/hash to avoid duplicate-URL signals).
    const canonical = `${window.location.origin}${pathname}`;
    setCanonical(canonical);
    setMeta("property", "og:url", canonical);
  }, [seo, pageSeo, lang, pathname]);
  return null;
}
